import { useEffect, useState } from "react";
import { useRemoveMutation } from "../../../../features/usersApi";
import { Spinner } from "../../../ui/molecules/spinner";
import { ConfirmModal } from "../../../ui/organisms/confirmModal.tsx";
import { Pagination } from "../../../ui/organisms/pagination";
import { ItemsPerPageValues } from "../../../ui/organisms/pagination/dto";
import { UserDTO } from "../dto";
import { CantDeleteModal } from "./cantDeleteModal";
import { UsersTable } from "./table";
import { extractDeleteUserErrorInfo } from "./errorUtils";

interface Props {
  users: {
    user: UserDTO;
    avatar: string | null;
  }[]
  total: number
  page: number
  itemsPerPage: ItemsPerPageValues
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: ItemsPerPageValues) => void
  onEdit: (id: string) => void
  onDeleteSuccess?: (id: string) => void
  onDeleteError?: (error: any, id: string) => void
  // Props per ordinamento - SOLO quello che serve alla table
  sortField?: string
  sortDirection: 'asc' | 'desc'
  onSort: (field: string) => void
  isLoading?: boolean
}

export function UsersList({
  users,
  total,
  page,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
  sortField,
  sortDirection,
  onSort,
  isLoading = false
}: Props) {
  const [idToDelete, setIdToDelete] = useState<string | undefined>();
  const [showCantDeleteModal, setShowCantDeleteModal] = useState(false);
  const [cantDeleteProjects, setCantDeleteProjects] = useState<string[]>([]);
  const [cantDeleteTargets, setCantDeleteTargets] = useState<string[]>([]);

  const [removeUser, {
    isLoading: isDeleting,
    reset: resetMutation
  }] = useRemoveMutation();

  const handleDeleteConfirm = async () => {
    if (!idToDelete) return;

    try {
      await removeUser(idToDelete).unwrap();

      setIdToDelete(undefined);
      onDeleteSuccess?.(idToDelete);

    } catch (error: any) {
      setIdToDelete(undefined);

      const { projects, targets, isTargetListsError } = extractDeleteUserErrorInfo(error);

      if (isTargetListsError) {
        setCantDeleteProjects(projects);
        setCantDeleteTargets(targets);
        setShowCantDeleteModal(true);
      }

      onDeleteError?.(error, idToDelete);
    }
  };

  useEffect(() => {
    return () => {
      resetMutation();
    };
  }, [resetMutation]);

  const handleCloseDeleteModal = () => {
    setIdToDelete(undefined);
    resetMutation();
  };

  const handleCloseCantDeleteModal = () => {
    setShowCantDeleteModal(false);
    setCantDeleteProjects([]);
    setCantDeleteTargets([]);
  };

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center h-[45%]">
        <Spinner />
      </div>
    );
  }

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="w-[100%] flex flex-col" style={{ height: 'calc(100% - 64px)' }}>
      <UsersTable
        data={users}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        onEdit={onEdit}
        onDelete={setIdToDelete}
      />

      <Pagination
        itemsPerPage={itemsPerPage}
        setItemsPerPage={onItemsPerPageChange}
        found={total}
        pages={totalPages}
        currentPage={page}
        setPage={onPageChange}
      />
      <CantDeleteModal
        visible={showCantDeleteModal}
        onClose={handleCloseCantDeleteModal}
        projects={cantDeleteProjects}
        targets={cantDeleteTargets}
      />
      <ConfirmModal
        title={"Elimina utente"}
        label={"Sei sicuro di voler eliminare questo utente?"}
        confirmAction={handleDeleteConfirm}
        onClose={handleCloseDeleteModal}
        visible={idToDelete !== undefined}
        loading={isDeleting}
      />
    </div>
  );
}
